import type { Player, PlayerId } from '@webgame/shared';
import { useCallback, useEffect, useState } from 'preact/hooks';
import { isApiError } from '../../core/api/api-client';
import type { AuthProviders } from '../../core/session/session-client';
import { LanguageSwitcher } from '../components/language-switcher';
import { useTranslator } from '../hooks/use-translator';
import { useBoot } from '../services';
import { PANEL_THEME } from '../game/panel-theme';
import { forgetWatch, watcherAmong } from '../dev/watch';
import { firstScreenShown } from './game-screen-loader';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'loaded'; providers: AuthProviders; seeded: Player[] }
  | { kind: 'failed'; code: string };

const errorCode = (error: unknown): string => (isApiError(error) ? error.code : 'unknown');

/**
 * Sign-in (ADR 0029): Google when the server offers it; with dev tools, a development account
 * by name and the seeded characters (each browser tab plays one).
 */
export function SignInScreen(props: { error?: string | undefined }) {
  const { session: client } = useBoot();
  const translator = useTranslator();
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  const [pending, setPending] = useState<PlayerId | 'dev' | null>(null);
  const [error, setError] = useState<string | null>(props.error ?? null);
  const [devAccount, setDevAccount] = useState('');

  const fetchProviders = useCallback(() => {
    setLoad({ kind: 'loading' });
    client
      .authProviders()
      .then(async (providers) => ({
        providers,
        seeded: providers.devSignIn ? await client.listPlayers() : [],
      }))
      .then(
        ({ providers, seeded }) => {
          setLoad({ kind: 'loaded', providers, seeded });
          firstScreenShown();
        },
        (failure: unknown) => {
          setLoad({ kind: 'failed', code: errorCode(failure) });
          firstScreenShown();
        },
      );
  }, [client]);

  useEffect(fetchProviders, [fetchProviders]);

  const run = (key: PlayerId | 'dev', action: () => Promise<void>) => {
    setPending(key);
    setError(null);
    action().catch((failure: unknown) => {
      setPending(null);
      setError(errorCode(failure));
    });
  };

  // Back from F4's «watch in god mode» (B131): in as the scenario's first human, once.
  const watcher = load.kind === 'loaded' ? watcherAmong(load.seeded) : null;
  useEffect(() => {
    if (!watcher) return;
    setPending(watcher.id);
    client.signIn(watcher.id).catch((failure: unknown) => {
      forgetWatch();
      setPending(null);
      setError(errorCode(failure));
    });
  }, [client, watcher]);

  return (
    <main class="screen screen--centered" data-testid="sign-in">
      <header class="screen__toolbar">
        <LanguageSwitcher />
      </header>
      <section class="panel panel--narrow" data-theme={PANEL_THEME}>
        <h1 class="panel__title">{translator.t('screens.signIn.title')}</h1>
        <p class="panel__subtitle">{translator.t('screens.signIn.subtitle')}</p>

        {error && (
          <p class="notice notice--error" role="alert">
            {translator.error(error)}
          </p>
        )}

        {load.kind === 'loading' && <p class="muted">{translator.t('common.loading')}</p>}

        {load.kind === 'failed' && (
          <div class="notice notice--error" role="alert">
            <p>{translator.t('screens.signIn.loadFailed')}</p>
            <p class="muted">{translator.error(load.code)}</p>
            <button type="button" class="button" onClick={fetchProviders}>
              {translator.t('common.retry')}
            </button>
          </div>
        )}

        {load.kind === 'loaded' && (
          <>
            {load.providers.google && (
              <a
                class="button button--primary button--block"
                href={load.providers.google.startUrl}
                data-testid="sign-in-google"
              >
                {translator.t('screens.signIn.google')}
              </a>
            )}
            {!load.providers.google && !load.providers.devSignIn && (
              <p class="notice">{translator.t('screens.signIn.noProviders')}</p>
            )}

            {load.providers.devSignIn && (
              <section class="panel__section" aria-labelledby="dev-sign-in">
                <h2 class="panel__section-title" id="dev-sign-in">
                  {translator.t('screens.signIn.devTitle')}
                </h2>
                <form
                  class="field"
                  onSubmit={(event) => {
                    event.preventDefault();
                    const subject = devAccount.trim();
                    if (subject) run('dev', () => client.signInAsDev(subject));
                  }}
                >
                  <label class="field__label" for="dev-account">
                    {translator.t('screens.signIn.devAccountLabel')}
                  </label>
                  <div class="form-row">
                    <input
                      id="dev-account"
                      data-testid="dev-account"
                      maxLength={64}
                      value={devAccount}
                      onInput={(event) => {
                        setDevAccount(event.currentTarget.value);
                      }}
                    />
                    <button
                      type="submit"
                      class="button"
                      data-testid="dev-account-submit"
                      disabled={pending !== null || devAccount.trim() === ''}
                      aria-busy={pending === 'dev'}
                    >
                      {translator.t('screens.signIn.devAccountSubmit')}
                    </button>
                  </div>
                  <span class="field__hint">{translator.t('screens.signIn.devAccountHint')}</span>
                </form>

                <h3 class="field__label">{translator.t('screens.signIn.seededTitle')}</h3>
                {load.seeded.length === 0 ? (
                  <p class="notice">{translator.t('screens.signIn.empty')}</p>
                ) : (
                  <ul class="player-list">
                    {load.seeded.map((player) => (
                      <li key={player.id} class="player-card">
                        <div class="player-card__info">
                          <span class="player-card__name">{player.displayName}</span>
                          <span class={`tag ${player.isBot ? 'tag--bot' : 'tag--human'}`}>
                            {translator.t(
                              player.isBot ? 'screens.signIn.botTag' : 'screens.signIn.humanTag',
                            )}
                          </span>
                        </div>
                        <button
                          type="button"
                          class="button button--primary"
                          data-testid={`sign-in-${player.displayName}`}
                          disabled={pending !== null}
                          aria-busy={pending === player.id}
                          onClick={() => {
                            run(player.id, () => client.signIn(player.id));
                          }}
                        >
                          {pending === player.id
                            ? translator.t('common.pending')
                            : translator.t('screens.signIn.signInAs', {
                                name: player.displayName,
                              })}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}
          </>
        )}
      </section>
    </main>
  );
}
